import { ReactNode, useState } from "react";
import { Trash2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzReportRemoveAssessmentMutation } from "@/services/backend/htz/report/api/report-api";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";

// The lists of the report the dialog removes assessments from.
const assessmentLists = [
  "structuralAssessments",
  "visualAssessments",
  "microbialAssessments",
  "photos",
];

/**
 * ReportAssessmentRemoveDialog removes an assessment from the report. It
 * serves every kind of assessment - structural, visual, microbial and
 * photo - since the backend removes them through one route.
 *
 * The dialog is uncontrolled by default. Pass `open` and `onOpenChange` to
 * control it, e.g. when it is opened from a dropdown menu whose content
 * unmounts on close; no trigger is rendered then.
 */
export function ReportAssessmentRemoveDialog({
  reportId,
  assessmentId,
  title,
  description,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onRemoved,
  disabled,
}: {
  reportId: UUID;
  assessmentId: UUID;
  title?: string;
  description?: string;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRemoved?: () => void;
  disabled?: boolean;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [remove, { isLoading, error, reset }] =
    useHtzReportRemoveAssessmentMutation();
  const { resetFields } = useErrValidationFailedContext();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    remove({ id: reportId, assessmentId })
      .unwrap()
      .then(() => {
        toast.success(t("Beurteilung gelöscht"));
        assessmentLists.forEach((list) =>
          resetFields(joinFieldName(list, assessmentId)),
        );
        reset();
        setOpen(false);
        onRemoved?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled &&
        (trigger || (
          <CommonDialogTrigger
            icon={Trash2}
            label=""
            variant="ghost"
            size="sm"
            disabled={disabled}
          />
        ))}
      <DialogContent>
        <CommonDialogHeader
          title={title ?? t("Beurteilung wirklich löschen?")}
          description={
            description ??
            t(
              "Die Beurteilung wird endgültig aus dem Anlagenbericht gelöscht. Das Ergebnis der Komponente wird neu berechnet.",
            )
          }
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
